import { type Placement } from "@floating-ui/react-dom";
import { EditorContent } from "@tiptap/react";

import { cn } from "@btnet/utilities-core";

import { lazyLoadWithRetry } from "utilities/react.utilities";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { BrowseImagesOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/BrowseImagesOverlay/BrowseImagesOverlay";
import { LinkEditOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/LinkEditOverlay/LinkEditOverlay";
import { LinkOptionsOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/LinkOptionsOverlay/LinkOptionsOverlay";
import { type EditorControlBreakpointNames } from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

import "./BTEditorContent.less";

const DebugEditorContent = lazyLoadWithRetry(
    () => import("commonComponents/btWrappers/editor/core/BTEditorContent/DebugEditorContent"),
);

interface IBTEditorContentProps {
    id: string;
    "data-testid": string;
    editor: IEditor;
    editorSize?: EditorControlBreakpointNames | null;
    readOnly?: boolean;
    hideImages?: boolean;
    linkOverlayPlacements?: Placement[];
}
export const BTEditorContent: React.FC<IBTEditorContentProps> = ({
    id,
    "data-testid": dataTestId,
    editor,
    editorSize = null,
    readOnly,
    hideImages,
    linkOverlayPlacements,
}) => {
    const { tiptapEditor } = editor;
    return (
        <div className={cn("BTEditorContent")}>
            {/* We can add editor overlays here */}
            {!readOnly && editor && <LinkOptionsOverlay editor={editor} />}
            {!readOnly && editor && (
                <LinkEditOverlay
                    editor={editor}
                    editorSize={editorSize}
                    fallbackPlacements={linkOverlayPlacements}
                />
            )}
            {!readOnly && editor && <BrowseImagesOverlay editor={editor} />}
            {/* The image editor overlays exists within image extension.
             * Why? Because it can ;). Less state management needed to
             * consolidate all of them in one place unless we absolutely have to
             */}

            {/* eslint-disable-next-line react/forbid-elements */}
            <EditorContent
                className={cn({
                    Editable: !readOnly,
                    HideImages: hideImages,
                })}
                id={id}
                data-testid={dataTestId}
                editor={tiptapEditor}
                readOnly={readOnly}
            />
            {import.meta.env.MODE === "development" && (
                <BTSuspense>
                    <DebugEditorContent editor={editor} />
                </BTSuspense>
            )}
        </div>
    );
};
